import { observer } from 'mobx-react'
import { createRef, FC, FocusEventHandler, useEffect } from 'react'
import { styled } from '../../../lib/stitches'
import { Box, Column } from '../../core/Components'
import { ChevronDown, ChevronUp } from '../../core/icons'
import { Input, InputContainer, Label, TempoContainer } from './common'
import TuneButton from './TuneButton'

export const TRANSPOSE_RANGE = [-60, 60]

const IconContainer = styled(Box, {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '9px',
  width: '18px'
})

const useTranspose = (onChange: (newValue: number) => void, value: number) => {
  const transposeRef = createRef<HTMLInputElement>()

  useEffect(() => {
    if (transposeRef.current) transposeRef.current.value = value.toString()
  }, [transposeRef.current, value])

  const updateTranspose = (transpose: string | number) => {
    let newTranspose
    try {
      newTranspose = Number(transpose)
    } catch {
      transposeRef.current.value = value.toString()
      return
    }
    newTranspose = Math.max(Math.min(newTranspose, TRANSPOSE_RANGE[1]), TRANSPOSE_RANGE[0])
    onChange(newTranspose)
    if (transposeRef.current) transposeRef.current.value = newTranspose
  }

  const handleBlur: FocusEventHandler<HTMLInputElement> = (e) => updateTranspose(e.target.value)

  return { transposeRef, updateTranspose, handleBlur }
}

interface Props {
  value: number
  onChange: (newValue: number) => void
  showTuneButton?: boolean
  onTuneClick?: () => void
}

const Transpose: FC<Props> = ({ value, onChange, showTuneButton, onTuneClick }) => {
  const { transposeRef, updateTranspose, handleBlur } = useTranspose(onChange, value)
  return (
    <TempoContainer>
      <Label>Transpose</Label>
      <InputContainer>
        {showTuneButton && <TuneButton onClick={onTuneClick} />}
        <Input ref={transposeRef} onBlur={handleBlur} />
        <Column verticalCenter>
          <IconContainer onClick={() => updateTranspose(value + 1)}>
            <ChevronUp height="9px" width="18px" />
          </IconContainer>
          <IconContainer onClick={() => updateTranspose(value - 1)}>
            <ChevronDown height="9px" width="18px" />
          </IconContainer>
        </Column>
      </InputContainer>
    </TempoContainer>
  )
}

export default observer(Transpose)
